import { useSelector } from "react-redux";

import { RootState } from "@/types/store";
import { UserType } from "@/types/user";
import { PERMISSION_MAPPING, USER_TYPES } from "@/utils/constants";
import { hasPermission } from "@/lib/utils";

type USER_TYPES_LIST = (typeof USER_TYPES)[keyof typeof USER_TYPES];

type AuthUserPermissionProps = {
  userTypes: USER_TYPES_LIST[];
  requiredPermissions?: Array<
    (typeof PERMISSION_MAPPING)[keyof typeof PERMISSION_MAPPING]
  >;
};

type AuthUserPermissionResponse = {
  isUserType: boolean;
  hasRequiredPermissions: boolean;
};

/**
 * Checking permission and user role
 * @param userTypes - Array of allowed user types
 * @param requiredPermissions - Array of required permissions
 */
export const useAuthUserPermission = (
  props: AuthUserPermissionProps
): AuthUserPermissionResponse => {
  const { userTypes, requiredPermissions = [] } = props;

  // Null before sign-in; no user type matches and no permission is granted.
  const userType = useSelector((state: RootState) => state.Auth.userType) ?? "";
  const adminPermissions = useSelector(
    (state: RootState) => state.Auth.permissions
  );

  const hasRequiredPermissions = requiredPermissions.every((permission) =>
    hasPermission(userType, adminPermissions || [], permission)
  );

  return {
    isUserType: userTypes.some((allowed) => allowed === userType),
    hasRequiredPermissions,
  };
};

export const useAuthData = () => {
  const isAuthorized = useSelector(
    (state: RootState) => state.Auth.isAuthorized
  );
  const user = useSelector((state: RootState) => state.Auth.user);
  const status = useSelector((state: RootState) => state.Auth.user?.status);
  const userEmail = useSelector((state: RootState) => state.Auth.userEmail);
  // Auth state holds a null userType before sign-in, but this hook is only
  // consumed from authenticated trees, where one is always set. Narrowing here
  // keeps that assumption in one reviewable place instead of at every caller.
  const userType = useSelector(
    (state: RootState) => state.Auth.userType
  ) as UserType;
  const permissions = useSelector((state: RootState) => state.Auth.permissions);
  const rememberMe = useSelector((state: RootState) => state.Auth.rememberMe);
  const rememberedUsers = useSelector(
    (state: RootState) => state.Auth.rememberedUsers
  );
  const questionnaireData = useSelector(
    (state: RootState) => state.Auth.questionnaireData
  );

  return {
    isAuthorized,
    user,
    userType,
    userEmail,
    permissions,
    status,
    rememberMe,
    rememberedUsers,
    questionnaireData,
  };
};

export const selectAuthLoading = (state: RootState) => state.Auth.loading;
